import { useCallback, useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useBrandThemeColors } from "../../../branding";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAssessmentNameOptions } from "../../../hooks/useAssessmentNameOptions";
import {
  buildAutoTaggingPlanSubmitItem,
  updateAutoTaggingPlan,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import DiscardEnhancedTaggingRuleChangesDialog from "../EnhancedTaggingRule/DiscardEnhancedTaggingRuleChangesDialog";
import type { TaggingPlanRow } from "../constants";
import AssessmentNameField from "./AssessmentNameField";
import { ASSESSMENT_PROMPT_DEFAULT_INFO } from "./AssessmentPromptField";
import AssessmentPromptFieldWithDefault from "./AssessmentPromptFieldWithDefault";
import ClassificationPlanConfigFields, {
  ClassificationPlanFlowArrow,
} from "./ClassificationPlanConfigFields";
import {
  buildAssessmentRecordItems,
  buildReferenceItemsFromObjectSubType,
  mapAssessmentTemplateToApi,
} from "./classificationPlanApiMapping";
import {
  CLASSIFICATION_PROMPT_MAX_LENGTH,
  parseClassificationLayer,
  shouldShowPrompt,
  shouldShowTaggingApproach,
} from "./classificationPlanConfigOptions";
import {
  ImpactedItemsCascadePicker,
  ReferenceItemsCascadePicker,
  type ImpactedCascadeSelection,
  type ReferenceCascadeSelection,
} from "./ClassificationCascadePicker";
import {
  assertEditComboAvailability,
  buildPlanLimitToastMessage,
  isConceptPlanLimitError,
  type ConceptPlanLimitItem,
} from "./conceptStatsValidation";
import DefaultAssessmentPromptPrefetch from "./DefaultAssessmentPromptPrefetch";
import {
  isEditFormDirty,
  type EditAssessmentPlanFormState,
} from "./editAssessmentPlanForm";
import {
  hasDraftItemFieldErrors,
  PLAN_LIMIT_CONCEPT_ERROR,
  validateDraftItem,
  type DraftItemFieldErrors,
} from "./validateDraftItem";

/** @deprecated Prefer `EditAssessmentPlanFormState` from `./editAssessmentPlanForm`. */
export type EditFormState = EditAssessmentPlanFormState;

function mapRowConceptValues(
  values: TaggingPlanRow["conceptValues"],
): ConceptValue[] {
  return values.map((value) => ({
    id: value.code,
    code: value.code,
    name: value.name.trim() || value.code,
    description: value.description,
  }));
}

function buildFormStateFromPlan(plan: TaggingPlanRow): EditAssessmentPlanFormState {
  return {
    objectType: plan.objectType,
    objectSubType: plan.objectSubType,
    objectTypeName: plan.objectTypeName,
    objectSubTypeName: plan.objectSubTypeName,
    referenceConceptCode: plan.referenceConceptCode || undefined,
    referenceConceptDisplayName:
      plan.referenceConceptDisplayName || undefined,
    referenceDimensions: plan.referenceDimensions.map((dim) => ({
      conceptCode: dim.conceptCode,
      conceptDisplayName: dim.conceptDisplayName,
      allValues: dim.allValues,
      values: dim.values.map((value) => ({
        id: value.code,
        code: value.code,
        name: value.name,
        description: value.description ?? "",
      })),
    })),
    conceptCode: plan.conceptCode,
    conceptValues: mapRowConceptValues(plan.conceptValues),
    assessmentPrompt: plan.description,
    allValues: plan.allValues,
    assessmentName: plan.name?.trim() || "",
    active: plan.enabled,
    taggingApproach: plan.taggingApproach,
    execution: plan.execution,
    layer:
      Number.isFinite(plan.layer) && plan.layer >= 0
        ? String(Math.floor(plan.layer))
        : "",
    assessmentTemplate: plan.assessmentTemplate,
    updateAssessmentRecordConcept: plan.updateAssessmentRecordConcept,
    updateAssessmentRecords: [...plan.updateAssessmentRecords],
  };
}

function toComboDraftItem(
  id: string,
  item: Pick<
    EditAssessmentPlanFormState,
    "objectType" | "objectSubType" | "conceptCode"
  >,
) {
  return {
    id,
    objectType: item.objectType,
    objectSubType: item.objectSubType,
    conceptCode: item.conceptCode,
  };
}

type EditAssessmentPlanItemDialogProps = Readonly<{
  open: boolean;
  plan: TaggingPlanRow | null;
  saving?: boolean;
  onClose: () => void;
  onSave: () => void;
  onSavingChange?: (saving: boolean) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}>;

export { isEditFormDirty } from "./editAssessmentPlanForm";

export default function EditAssessmentPlanItemDialog({
  open,
  plan,
  saving = false,
  onClose,
  onSave,
  onSavingChange,
  onError,
  onSuccess,
}: EditAssessmentPlanItemDialogProps) {
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  /** Assessment Name options load on dropdown open only (not when dialog opens). */
  const [assessmentNameMenuActive, setAssessmentNameMenuActive] = useState(false);
  const [assessmentNameReloadToken, setAssessmentNameReloadToken] = useState(0);
  const { options: assessmentNameOptions, loading: assessmentNameOptionsLoading } =
    useAssessmentNameOptions({
      enabled: assessmentNameMenuActive,
      reloadToken: assessmentNameReloadToken,
    });
  // Edit always has a concrete objectType on the plan; no need to prefetch /plans/types.
  const defaultObjectType = "";

  const [formState, setFormState] = useState<EditAssessmentPlanFormState | null>(
    null,
  );
  const [initialFormState, setInitialFormState] =
    useState<EditAssessmentPlanFormState | null>(null);
  const [fieldErrors, setFieldErrors] = useState<DraftItemFieldErrors>({});
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !plan) {
      setFormState(null);
      setInitialFormState(null);
      setFieldErrors({});
      setCheckingLimit(false);
      setDiscardConfirmOpen(false);
      return;
    }

    const next = buildFormStateFromPlan(plan);
    setFormState(next);
    setInitialFormState(next);
    setFieldErrors({});
    setCheckingLimit(false);
    setDiscardConfirmOpen(false);
  }, [open, plan]);

  const updateFormState = useCallback(
    (patch: Partial<EditAssessmentPlanFormState>) => {
      setFormState((current) => {
        if (!current) return current;
        const next = { ...current, ...patch };
        // Template switch changes default-prompt text — clear typed prompt so
        // the grey suggestion can show Tagging vs Implementation defaults.
        if (
          patch.assessmentTemplate !== undefined &&
          patch.assessmentPrompt === undefined
        ) {
          next.assessmentPrompt = "";
        }
        return next;
      });
      setFieldErrors((current) => {
        const next = { ...current };
        if (patch.objectType !== undefined) delete next.objectType;
        if (patch.objectSubType !== undefined) delete next.objectSubType;
        if (patch.conceptCode !== undefined) delete next.conceptCode;
        if (patch.conceptValues !== undefined || patch.allValues !== undefined) {
          delete next.conceptValues;
        }
        if (patch.assessmentPrompt !== undefined) delete next.assessmentPrompt;
        if (patch.taggingApproach !== undefined) delete next.taggingApproach;
        if (patch.execution !== undefined) delete next.execution;
        if (patch.layer !== undefined) delete next.layer;
        if (patch.assessmentTemplate !== undefined) {
          delete next.assessmentTemplate;
          delete next.assessmentPrompt;
        }
        return next;
      });
    },
    [],
  );

  const isDirty = useMemo(() => {
    if (!formState || !initialFormState) return false;
    return isEditFormDirty(formState, initialFormState);
  }, [formState, initialFormState]);

  const handleRequestClose = useCallback(() => {
    if (saving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    onClose();
  }, [isDirty, onClose, saving]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const handlePlanLimitReached = useCallback(
    (items: ConceptPlanLimitItem[], toastMessage?: string) => {
      if (items.length === 0) return;

      showMessage(toastMessage ?? buildPlanLimitToastMessage(items), "error");
      updateFormState({
        conceptCode: "",
        conceptValues: [],
        assessmentPrompt: "",
        allValues: false,
      });
      setFieldErrors((current) => ({
        ...current,
        conceptCode: PLAN_LIMIT_CONCEPT_ERROR,
      }));
    },
    [showMessage, updateFormState],
  );

  const checkComboLimit = useCallback(
    async (next: EditAssessmentPlanFormState) => {
      if (!plan || !next.conceptCode.trim()) return;

      setCheckingLimit(true);
      try {
        await assertEditComboAvailability(
          toComboDraftItem(plan.id, next),
          toComboDraftItem(plan.id, {
            objectType: plan.objectType,
            objectSubType: plan.objectSubType,
            conceptCode: plan.conceptCode,
          }),
          defaultObjectType,
        );
      } catch (error) {
        if (isConceptPlanLimitError(error)) {
          handlePlanLimitReached(error.items, error.message);
        } else {
          showMessage(
            error instanceof Error
              ? error.message
              : "Failed to check plan availability.",
            "warning",
          );
        }
      } finally {
        setCheckingLimit(false);
      }
    },
    [defaultObjectType, handlePlanLimitReached, plan, showMessage],
  );

  const handleReferenceChange = useCallback(
    (next: ReferenceCascadeSelection) => {
      if (!formState) return;
      const objectTypeChanged = next.objectType !== formState.objectType;
      const referenceChanged =
        objectTypeChanged ||
        next.objectSubType !== formState.objectSubType ||
        (next.referenceConceptCode ?? "") !==
          (formState.referenceConceptCode ?? "") ||
        JSON.stringify(next.referenceDimensions ?? []) !==
          JSON.stringify(formState.referenceDimensions ?? []);
      if (referenceChanged) {
        const clearTagApproach = !shouldShowTaggingApproach(
          next.objectType,
          next.objectTypeName,
        );
        updateFormState({
          objectType: next.objectType,
          objectSubType: next.objectSubType,
          objectTypeName: next.objectTypeName,
          objectSubTypeName: next.objectSubTypeName,
          referenceConceptCode: next.referenceConceptCode,
          referenceConceptDisplayName: next.referenceConceptDisplayName,
          referenceDimensions: next.referenceDimensions,
          // Reference drives assessment_record concepts — reset selection.
          updateAssessmentRecordConcept: "",
          updateAssessmentRecords: [],
          ...(objectTypeChanged
            ? {
                conceptCode: "",
                conceptValues: [],
                assessmentPrompt: "",
                allValues: false,
                ...(clearTagApproach ? { taggingApproach: "" } : {}),
              }
            : {}),
        });
        return;
      }
      updateFormState({
        objectType: next.objectType,
        objectSubType: next.objectSubType,
        referenceConceptCode: next.referenceConceptCode,
        referenceConceptDisplayName: next.referenceConceptDisplayName,
        referenceDimensions: next.referenceDimensions,
      });
    },
    [formState, updateFormState],
  );

  const handleImpactedChange = useCallback(
    (next: ImpactedCascadeSelection) => {
      if (!formState) return;
      const nextState: EditAssessmentPlanFormState = {
        ...formState,
        conceptCode: next.conceptCode,
        conceptValues: next.allValues ? [] : next.conceptValues,
        allValues: next.allValues,
        assessmentPrompt: "",
      };
      updateFormState({
        conceptCode: next.conceptCode,
        conceptValues: next.allValues ? [] : next.conceptValues,
        allValues: next.allValues,
        assessmentPrompt: "",
      });
      void checkComboLimit(nextState);
    },
    [checkComboLimit, formState, updateFormState],
  );

  const handleSave = useCallback(async () => {
    if (!plan || !formState || saving || checkingLimit) return;

    const assessmentPrompt = formState.assessmentPrompt.trim();
    const assessmentName = (formState.assessmentName ?? "").trim();

    const errors = validateDraftItem(
      {
        assessmentName,
        objectType: formState.objectType,
        objectSubType: formState.objectSubType,
        objectTypeName: formState.objectTypeName,
        conceptCode: formState.conceptCode,
        conceptValues: formState.conceptValues,
        assessmentPrompt,
        allValues: formState.allValues,
        taggingApproach: formState.taggingApproach,
        execution: formState.execution,
        layer: formState.layer,
        assessmentTemplate: formState.assessmentTemplate,
      },
      {
        // Concept V3: Reference Object alone is enough; Doc Type / Value optional.
        requireObjectSubType: false,
      },
    );
    if (hasDraftItemFieldErrors(errors)) {
      setFieldErrors(errors);
      if (errors.assessmentName) {
        showMessage("Please enter Assessment Name.", "error");
      }
      return;
    }

    onSavingChange?.(true);
    try {
      await assertEditComboAvailability(
        toComboDraftItem(plan.id, formState),
        toComboDraftItem(plan.id, {
          objectType: plan.objectType,
          objectSubType: plan.objectSubType,
          conceptCode: plan.conceptCode,
        }),
        defaultObjectType,
      );

      const payload = buildAutoTaggingPlanSubmitItem({
        objectType: formState.objectType,
        objectSubType: formState.objectSubType,
        conceptCode: formState.conceptCode,
        conceptValues: formState.allValues ? [] : formState.conceptValues,
        assessmentPrompt,
        assessmentName,
        allValues: formState.allValues,
        active: formState.active,
        layer: parseClassificationLayer(formState.layer) ?? 0,
        referenceItems: buildReferenceItemsFromObjectSubType({
          objectSubType: formState.objectSubType,
          objectSubTypeName: formState.objectSubTypeName,
          referenceConceptCode: formState.referenceConceptCode,
          referenceDimensions: formState.referenceDimensions,
        }),
        assessmentRecordItems: buildAssessmentRecordItems(
          formState.updateAssessmentRecords,
          formState.updateAssessmentRecordConcept,
        ),
        assessmentType: mapAssessmentTemplateToApi(
          formState.assessmentTemplate,
        ),
      });

      await updateAutoTaggingPlan(plan.id, payload);
      onSuccess?.("Item saved successfully.");
      onSave();
    } catch (error) {
      if (isConceptPlanLimitError(error)) {
        handlePlanLimitReached(error.items, error.message);
      } else {
        onError?.(
          error instanceof Error
            ? error.message
            : "Failed to update Classification.",
        );
      }
    } finally {
      onSavingChange?.(false);
    }
  }, [
    checkingLimit,
    defaultObjectType,
    formState,
    handlePlanLimitReached,
    onError,
    onSave,
    onSavingChange,
    onSuccess,
    plan,
    saving,
    showMessage,
  ]);

  const referenceError =
    fieldErrors.objectType || fieldErrors.objectSubType || undefined;
  const impactedError =
    fieldErrors.conceptCode || fieldErrors.conceptValues || undefined;

  const showPromptField = shouldShowPrompt({
    objectType: formState?.objectType ?? "",
    conceptCode: formState?.conceptCode ?? "",
    allValues: formState?.allValues,
    conceptValues: formState?.conceptValues,
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(handleRequestClose)}
        maxWidth={false}
        // Avoid autofocus on open — otherwise Assessment Name shows a blue focus ring.
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: "8px",
            width: 816,
            maxWidth: "100%",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, pt: 3, px: 3, pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#101828" }}
            >
              Edit Classification Plan Item
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={handleRequestClose}
              disabled={saving}
              sx={{
                ...DIALOG_CLOSE_BUTTON_SX,
                position: "absolute",
                right: 16,
                top: 16,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 3, md: 6 }, pt: 4, pb: 1 }}>
          {formState ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <AssessmentNameField
                value={formState.assessmentName ?? ""}
                onChange={(next) => {
                  updateFormState({ assessmentName: next });
                  if (fieldErrors.assessmentName && next.trim()) {
                    setFieldErrors((prev) => {
                      const { assessmentName: _removed, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                options={assessmentNameOptions}
                loading={assessmentNameOptionsLoading}
                disabled={saving}
                error={Boolean(fieldErrors.assessmentName)}
                helperText={fieldErrors.assessmentName}
                onOpen={() => {
                  setAssessmentNameMenuActive(true);
                  setAssessmentNameReloadToken((token) => token + 1);
                }}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0,1fr) 44px minmax(0,1fr)",
                  },
                  gap: { xs: 1.5, md: "28px" },
                  // Top-align so Impacted helper/error text does not sink
                  // Reference / push config fields into a staggered layout.
                  alignItems: "start",
                  mt: 1.75,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <ReferenceItemsCascadePicker
                    value={{
                      objectType: formState.objectType,
                      objectSubType: formState.objectSubType,
                      objectTypeName:
                        formState.objectTypeName ?? plan?.objectTypeName,
                      objectSubTypeName:
                        formState.objectSubTypeName ?? plan?.objectSubTypeName,
                      referenceConceptCode: formState.referenceConceptCode,
                      referenceConceptDisplayName:
                        formState.referenceConceptDisplayName,
                      referenceDimensions: formState.referenceDimensions,
                      conceptCode: formState.conceptCode,
                      conceptValues: formState.conceptValues,
                      allValues: formState.allValues,
                    }}
                    error={Boolean(referenceError)}
                    helperText={referenceError}
                    disabled={saving}
                    onChange={handleReferenceChange}
                  />
                </Box>
                <ClassificationPlanFlowArrow />
                <Box sx={{ minWidth: 0 }}>
                  <ImpactedItemsCascadePicker
                    value={{
                      conceptCode: formState.conceptCode,
                      conceptValues: formState.allValues
                        ? []
                        : formState.conceptValues,
                      allValues: formState.allValues,
                    }}
                    objectType={formState.objectType}
                    objectSubType={formState.objectSubType}
                    error={Boolean(impactedError)}
                    helperText={impactedError}
                    disabled={saving}
                    onChange={handleImpactedChange}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  borderTop: "1px solid #F0F1F3",
                  mt: 2.75,
                  mb: 2.25,
                }}
              />

              <ClassificationPlanConfigFields
                value={{
                  taggingApproach: formState.taggingApproach,
                  execution: formState.execution,
                  layer: formState.layer,
                  assessmentTemplate: formState.assessmentTemplate,
                  updateAssessmentRecordConcept:
                    formState.updateAssessmentRecordConcept,
                  updateAssessmentRecords: formState.updateAssessmentRecords,
                }}
                objectType={formState.objectType}
                objectTypeName={formState.objectTypeName}
                disabled={saving}
                errors={{
                  taggingApproach: fieldErrors.taggingApproach,
                  execution: fieldErrors.execution,
                  layer: fieldErrors.layer,
                  assessmentTemplate: fieldErrors.assessmentTemplate,
                }}
                onChange={(patch) => updateFormState(patch)}
              />

              {showPromptField ? (
                <DefaultAssessmentPromptPrefetch
                  conceptCode={formState.conceptCode}
                  objectType={formState.objectType}
                  assessmentTemplate={formState.assessmentTemplate}
                />
              ) : null}

              {showPromptField ? (
                <Box sx={{ mt: 1.75 }}>
                  <AssessmentPromptFieldWithDefault
                    conceptCode={formState.conceptCode}
                    objectType={formState.objectType}
                    assessmentTemplate={formState.assessmentTemplate}
                    value={formState.assessmentPrompt}
                    error={Boolean(fieldErrors.assessmentPrompt)}
                    helperText={fieldErrors.assessmentPrompt}
                    maxLength={CLASSIFICATION_PROMPT_MAX_LENGTH}
                    required={false}
                    defaultPromptInfoText={ASSESSMENT_PROMPT_DEFAULT_INFO}
                    onChange={(nextValue) =>
                      updateFormState({ assessmentPrompt: nextValue })
                    }
                  />
                </Box>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 3, gap: 2.5 }}>
          <Button
            variant="text"
            onClick={handleRequestClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              fontSize: "0.8125rem",
              color: primaryColor,
              px: 1.25,
              "&:hover": {
                textDecoration: "underline",
                bgcolor: "transparent",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving || checkingLimit || !formState}
            sx={{
              ...brandContainedButtonSx,
              bgcolor: `${primaryColor} !important`,
              borderRadius: "3px",
              px: 2.25,
              py: 1,
              fontSize: "0.8125rem",
              "&:hover": {
                bgcolor: `${primaryColor} !important`,
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />
    </>
  );
}
